import React, { useCallback, useEffect, useState } from 'react';

import { debounce } from '@mui/material';

import {
  AdminLegendVariant,
  searchUsers,
  adminValuesToBeParsed,
  AdminAllowanceNames,
  ConfirmationTypes,
  adminResetValue,
  HelpTypes,
  adminActions,
  successSelector,
  resetStatus,
  ServiceTypes,
} from '@pharmaplan/common';
import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import ConfirmationModal from '../../../components/Modals/ConfirmationModal';
import { setDialog } from '../../../reducers/dialogReducer';

import { useAppDispatch } from '../../useAppDispatch';
import { useAppSelector } from '../../useAppSelector';
import { showSuccess } from '../../../components/Admin/Profile/Pharmacist/PharmacistViewProfile/helper';
import strings from '../../../localization';
import { resetDrawer } from '../../../actions/drawerActions';
import useAdminDashboard from '../useAdminDashboard';
import useExpressBookingTypes from './useExpressBookingTypes';
import useExpressBookingFormik from './useExpressBookingFormik';
import { resetSecondaryRoute } from '../../../actions/navbarActions';

const { booked } = AdminLegendVariant;
const { self, full } = ServiceTypes;

const serviceType = ServiceHandler.getService();

const useExpressBooking = (
  type: Exclude<AdminLegendVariant, AdminLegendVariant.total>,
  id?: string,
  noActionsScreen?: boolean,
) => {
  const dispatch = useAppDispatch();

  const autocompleteConfig = {
    [self]: [HelpTypes.pharmacist, HelpTypes.technicians, HelpTypes.students],
    [full]: [HelpTypes.pharmacist],
  };
  const autocompleteType = autocompleteConfig[serviceType];

  const [expressValues, setExpressValues] = useState([
    ...adminValuesToBeParsed,
  ]);

  const { dashBasedRequest } = useAdminDashboard();
  const { detailsApiMap } = useExpressBookingTypes();
  const { getFormikInitialValues, bookWorkshift } = useExpressBookingFormik(
    type,
    expressValues,
    id,
  );

  const isBooked = type === booked;

  const success = useAppSelector((state) =>
    successSelector([adminActions.expressBooking], state));

  const confirmAction = (successAction: () => void) => {
    dispatch(
      setDialog({
        heading: {
          title: '',
        },
        Component: (
          <ConfirmationModal
            type={ConfirmationTypes.expressBooking}
            showCloseButton
            confirmAction={successAction}
          />
        ),
        showCloseButton: false,
      }),
    );
  };

  const getInput = (input: string) => {
    if (input) {
      dispatch(
        searchUsers({
          userTypes: autocompleteType,
          query: input,
        }),
      );
    }
  };

  const getPharmacyInput = (input: string) => {
    if (input) {
      dispatch(
        searchUsers({
          userTypes: [HelpTypes.pharmacy],
          query: input,
          showAppointedPharmacies: true,
        }),
      );
    }
  };

  useEffect(() => {
    if (id) {
      dispatch(detailsApiMap[type].api({ id }));
    }
    if (isBooked) {
      setExpressValues((prev) =>
        [
          ...prev,
          AdminAllowanceNames.pharmacyHourlyRate,
        ]);
    }

    return () => {
      dispatch(adminResetValue(['searchedPharmacists', 'searchedPharmacies']));
      dispatch(adminResetValue([detailsApiMap[type].state]));
      dispatch(resetSecondaryRoute());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const successMsg = id && isBooked
    ? strings.bookingEditedSuccessfully
    : strings.bookingCreatedSuccessfully;

  const successRequest = () => {
    if (!noActionsScreen) {
      dashBasedRequest();
    }
  };

  useEffect(() => {
    if (success) {
      successRequest();
      showSuccess(dispatch, successMsg);
      dispatch(resetStatus([adminActions.expressBooking]));
      dispatch(resetDrawer());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [success]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedInput = useCallback(debounce(getInput, 300), []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedPharmacyInput = useCallback(
    debounce(getPharmacyInput, 300),
    [],
  );

  return {
    confirmAction,
    detailsApiMap,
    debouncedInput,
    getFormikInitialValues,
    debouncedPharmacyInput,
    bookWorkshift,
  };
};

export default useExpressBooking;
